/**
 * CR-8719: what has been entered so far on the Collection, Credit and
 * Replacement tabs, shown the way the rest of the NCR page shows a saved value
 * -- the label above, the value inside a read-only box -- instead of a run of
 * "Label: value" text. One component for all three tabs, so they read alike.
 */
import type { ReactNode } from 'react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import type { NCRCollectionProcess } from '../../lib/api';

export interface ProcessDetailItem {
  label: string;
  /** The value to show; an empty one hides the field. */
  value?: string | null;
  /** Rendered in a read-only box instead of `value` (e.g. a document link). */
  node?: ReactNode;
  /** Notes: a full-width, multi-line box. */
  multiline?: boolean;
}

interface ProcessDetailsProps {
  /** Prefix for the field ids, unique per tab. */
  idPrefix: string;
  items: ProcessDetailItem[];
}

const hasValue = (i: ProcessDetailItem): boolean =>
  i.node != null || (i.value != null && String(i.value).trim() !== '');

/**
 * The read-only fields of a process, two to a row; notes span the row.
 *
 * @param idPrefix - prefix for the field ids
 * @param items - the fields in display order; empty ones are left out
 * @returns the fields, or null when none has a value
 */
export function ProcessDetails({ idPrefix, items }: ProcessDetailsProps) {
  const shown = items.filter(hasValue);
  if (shown.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-4" data-testid={`${idPrefix}-details`}>
      {shown.map((item, idx) => {
        const id = `${idPrefix}-detail-${idx}`;
        return (
          <div key={item.label} className={`space-y-2 ${item.multiline ? 'col-span-2' : ''}`}>
            <Label htmlFor={item.node ? undefined : id}>{item.label}</Label>
            {item.node ? (
              <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm">
                {item.node}
              </div>
            ) : item.multiline ? (
              <Textarea id={id} value={item.value ?? ''} rows={2} disabled readOnly />
            ) : (
              <Input id={id} value={item.value ?? ''} disabled readOnly />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * CR-8719: the collection address as fields -- the full address, or the one
 * free-text address a collection initiated before it holds.
 *
 * @param process - the collection process
 * @returns the address fields for ProcessDetails
 */
export function collectionAddressItems(process: NCRCollectionProcess): ProcessDetailItem[] {
  const structured: ProcessDetailItem[] = [
    { label: 'Address Line 1', value: process.collection_address_line_1 },
    { label: 'Address Line 2', value: process.collection_address_line_2 },
    { label: 'Town / City', value: process.collection_city },
    { label: 'County / State', value: process.collection_county },
    { label: 'Postcode', value: process.collection_postcode },
    { label: 'Country', value: process.collection_country },
  ];
  if (structured.some(hasValue)) return structured;
  return [{ label: 'Collection Address', value: process.collection_address, multiline: true }];
}
